/**
 * The columns lane of the stacking plan (spec 2026-10-25-21): every vertical
 * column of the building — riser, downpipe, stair cage, lift shaft — as a thin
 * bar running down the floor rows it crosses. Pure, so the spans and the
 * dashed gaps are unit-tested without a DOM.
 *
 * A column crosses a level when a floor plan draws its riser there, or when the
 * column declares it (`crosses_level`). Between its lowest and highest level, a
 * level it neither is drawn on nor declares is a gap: drawn dashed, continuity
 * not documented rather than an error.
 */
import { byColumnX, type RiserDiagram, type RiserDiagramColumn } from './riser-diagram-layout'

/** Width of one bar and the gap between two, in SVG units. */
export const COLUMN_BAR_WIDTH = 12
export const COLUMN_BAR_GAP = 6
/** Room left of the first bar, between the lots and the lane. */
export const COLUMN_LANE_PAD = 10
/** Past this many columns the lane stops growing; the rest are counted. */
export const MAX_LANE_COLUMNS = 12

export interface StackingColumnSpan {
    /** Row indexes in the plan's order (top floor first), inclusive. */
    fromRow: number
    toRow: number
    dashed: boolean
}

export interface StackingColumn {
    column: RiserDiagramColumn
    /** 0-based position in the lane. */
    lane: number
    spans: StackingColumnSpan[]
    /** The rows it crosses, top first: what the tooltip lists. */
    rows: number[]
}

export interface StackingColumnsLayout {
    columns: StackingColumn[]
    /** Columns past MAX_LANE_COLUMNS, not drawn. */
    hidden: number
    /** Lane width in SVG units, 0 when there is no column. */
    width: number
}

/** Width the lane takes for a number of columns, before any layout. */
export function laneWidth(count: number): number {
    const n = Math.min(count, MAX_LANE_COLUMNS)
    return n === 0 ? 0 : COLUMN_LANE_PAD + n * COLUMN_BAR_WIDTH + (n - 1) * COLUMN_BAR_GAP
}

/**
 * @param rowOfLevel floor asset uid → row index in the plan (rows merge every
 *   floor asset at one level, so several uids may share a row)
 */
export function layoutStackingColumns(
    diagram: Pick<RiserDiagram, 'columns'> | undefined,
    rowOfLevel: Map<string, number>,
): StackingColumnsLayout {
    const placed: Array<{ column: RiserDiagramColumn; rows: number[]; documented: Set<number> }> = []
    for (const column of diagram?.columns ?? []) {
        const documented = new Set<number>()
        for (const level of column.levels ?? []) {
            const row = rowOfLevel.get(level.level_uid)
            if (row === undefined) continue
            // A level counts when something is there: the drawn riser, the
            // declaration, or equipment hanging on the column at that level.
            const declared = level.declared === true
            if (level.riser || declared || (level.equipment ?? []).length > 0) documented.add(row)
        }
        if (documented.size === 0) continue
        const rows = [...documented].sort((a, b) => a - b)
        placed.push({ column, rows, documented })
    }
    // Stable reading order: the building-frame x when a plan places the
    // column (a section read left to right), else the name.
    placed.sort((a, b) => byColumnX(a.column, b.column))

    const shown = placed.slice(0, MAX_LANE_COLUMNS)
    const columns = shown.map(({ column, rows, documented }, lane) => {
        const spans: StackingColumnSpan[] = []
        const top = rows[0]
        const bottom = rows[rows.length - 1]
        let start = top
        let dashed = !documented.has(top)
        for (let r = top + 1; r <= bottom + 1; r++) {
            const isDashed = r <= bottom ? !documented.has(r) : !dashed
            if (r > bottom || isDashed !== dashed) {
                spans.push({ fromRow: start, toRow: r - 1, dashed })
                start = r
                dashed = isDashed
            }
        }
        return { column, lane, spans, rows }
    })
    return { columns, hidden: placed.length - shown.length, width: laneWidth(shown.length) }
}
